/**
 * Service pour valider les clés API Stripe côté client
 */

import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { httpsCallable, getFunctions } from 'firebase/functions';

export interface StripeKeyValidationResult {
    isValid: boolean;
    secretKeyValid: boolean;
    publishableKeyValid: boolean;
    webhookSecretValid: boolean;
    permissionsValid: boolean;
    isTestMode: boolean;
    accountId?: string;
    accountName?: string;
    errors: string[];
    warnings: string[];
    permissions: {
        detected: string[];
        dangerous: string[];
        missing: string[];
    };
    tests: {
        secretKey: {
            passed: boolean;
            message: string;
        };
        publishableKey: {
            passed: boolean;
            message: string;
        };
        webhookSecret: {
            passed: boolean;
            message: string;
        };
        permissions: {
            passed: boolean;
            message: string;
            details: string[];
        };
        paymentIntent: {
            passed: boolean;
            message: string;
        };
    };
}

export interface StripeKeyValidationRequest {
    secretKey: string;
    publishableKey?: string;
    webhookSecret?: string;
}

export class StripeKeyValidationService {
    private static readonly BASE_URL = FUNCTIONS_BASE_URL;
    private static functions = getFunctions(undefined, 'europe-west1');

    /**
     * Valide les clés Stripe déjà sauvegardées (via Secret Manager)
     */
    static async validateSavedKeys(organizationId: string): Promise<StripeKeyValidationResult> {
        try {
            const validateFn = httpsCallable<{ organizationId: string }, StripeKeyValidationResult>(
                this.functions,
                'validateSavedStripeApiKeys'
            );

            const result = await validateFn({ organizationId });
            return result.data;
        } catch (error: any) {
            console.error('Erreur lors de la validation des clés sauvegardées:', error);

            // Retourner un résultat d'erreur formaté
            return {
                isValid: false,
                secretKeyValid: false,
                publishableKeyValid: false,
                webhookSecretValid: false,
                permissionsValid: false,
                isTestMode: false,
                errors: [error.message || 'Erreur lors de la validation'],
                warnings: [],
                permissions: { detected: [], dangerous: [], missing: [] },
                tests: {
                    secretKey: { passed: false, message: 'Erreur d\'accès au coffre-fort' },
                    publishableKey: { passed: false, message: 'Non testé' },
                    webhookSecret: { passed: false, message: 'Non testé' },
                    permissions: { passed: false, message: 'Non testé', details: [] },
                    paymentIntent: { passed: false, message: 'Non testé' },
                },
            } as StripeKeyValidationResult;
        }
    }

    /**
     * Valide les clés API Stripe (avant sauvegarde)
     */
    static async validateKeys(request: StripeKeyValidationRequest): Promise<StripeKeyValidationResult> {
        try {
            const response = await fetch(`${this.BASE_URL}/validateStripeApiKeys`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
            });

            if (!response.ok) {
                let errorMessage = `Erreur HTTP: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch (e) {
                    // Si on ne peut pas parser le JSON, on garde le message par défaut
                }
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur lors de la validation des clés Stripe:', error);

            // Retourner un résultat d'erreur
            return {
                isValid: false,
                secretKeyValid: false,
                publishableKeyValid: false,
                webhookSecretValid: false,
                permissionsValid: false,
                isTestMode: false,
                errors: [error instanceof Error ? error.message : 'Erreur de connexion au serveur'],
                warnings: [],
                permissions: {
                    detected: [],
                    dangerous: [],
                    missing: [],
                },
                tests: {
                    secretKey: { passed: false, message: 'Erreur' },
                    publishableKey: { passed: false, message: 'Non testé' },
                    webhookSecret: { passed: false, message: 'Non testé' },
                    permissions: { passed: false, message: 'Non testé', details: [] },
                    paymentIntent: { passed: false, message: 'Non testé' },
                },
            } as StripeKeyValidationResult;
        }
    }

    /**
     * Teste si une clé secrète est valide (validation rapide sans test complet)
     * Accepte toutes les clés contenant _test_ ou _live_
     */
    static isValidSecretKeyFormat(key: string): boolean {
        return key.includes('_test_') || key.includes('_live_');
    }

    /**
     * Teste si une clé publique est valide (format)
     */
    static isValidPublishableKeyFormat(key: string): boolean {
        return key.includes('_test_') || key.includes('_live_');
    }

    /**
     * Teste si un webhook secret est valide (format)
     */
    static isValidWebhookSecretFormat(secret: string): boolean {
        return secret.startsWith('whsec_');
    }

    /**
     * Vérifie si les clés sont en mode test
     */
    static isTestMode(secretKey: string): boolean {
        return secretKey.includes('_test_');
    }

    /**
     * Vérifie la cohérence des modes entre clé secrète et publique
     */
    static areKeyModesConsistent(secretKey: string, publishableKey: string): boolean {
        const isSecretTest = secretKey.includes('_test_');
        const isPublicTest = publishableKey.includes('_test_');
        return isSecretTest === isPublicTest;
    }
}

export default StripeKeyValidationService;
